import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { workflowApi } from '../services/api';
import WorkflowMetrics from '../components/WorkflowMetrics';

interface Workflow {
  executionArn: string;
  stateMachineArn: string;
  name: string;
  status: string;
  startDate: string;
  stopDate?: string;
  workflowType: string;
}

const WorkflowList: React.FC = () => {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedWorkflowType, setSelectedWorkflowType] = useState('data-processing');
  const [selectedMetricsArn, setSelectedMetricsArn] = useState<string | null>(null);

  useEffect(() => {
    loadWorkflows();
  }, [selectedWorkflowType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await workflowApi.listWorkflows({ 
        workflowType: selectedWorkflowType
      });
      
      if (response.success) {
        setWorkflows(response.data || []);
      } else {
        setError(response.error || 'Failed to load workflows');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'running':
        return 'bg-blue-100 text-blue-800';
      case 'aborted':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (startDate: string, stopDate?: string) => {
    const start = new Date(startDate);
    const end = stopDate ? new Date(stopDate) : new Date();
    const duration = end.getTime() - start.getTime();
    
    if (duration < 1000) {
      return `${duration}ms`;
    } else if (duration < 60000) {
      return `${(duration / 1000).toFixed(1)}s`;
    } else if (duration < 3600000) {
      return `${(duration / 60000).toFixed(1)}m`;
    } else {
      return `${(duration / 3600000).toFixed(1)}h`;
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Workflows</h1>
          <p className="mt-2 text-gray-600">
            View and manage all your workflow executions
          </p>
        </div>
        <div className="flex space-x-4">
          <select
            value={selectedWorkflowType}
            onChange={(e) => setSelectedWorkflowType(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="data-processing">Data Processing</option>
            <option value="image-processing">Image Processing</option>
          </select>
          <Link
            to="/create"
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Create Workflow
          </Link>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading workflows</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <button
                onClick={loadWorkflows}
                className="mt-3 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedWorkflowType} Workflows ({workflows.length})
              </h2>
              <button
                onClick={loadWorkflows}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Refresh
              </button>
            </div>
          </div>
          
          {workflows.length === 0 ? (
            <div className="p-6">
              <p className="text-gray-500 text-center py-8">
                No {selectedWorkflowType} workflows found. Create your first workflow to get started.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Started
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {workflows.map((workflow) => (
                    <tr key={workflow.executionArn} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {workflow.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {workflow.workflowType}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(workflow.status)}`}>
                          {workflow.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDuration(workflow.startDate, workflow.stopDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(workflow.startDate).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <Link
                          to={`/workflows/${encodeURIComponent(workflow.executionArn)}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                        </Link>
                        <button
                          onClick={() => setSelectedMetricsArn(workflow.executionArn)}
                          className="text-green-600 hover:text-green-900"
                        >
                          📊 Metrics
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Metrics Modal */}
      {selectedMetricsArn && (
        <WorkflowMetrics
          executionArn={selectedMetricsArn}
          onClose={() => setSelectedMetricsArn(null)}
        />
      )}
    </div>
  );
};

export default WorkflowList;
